import { computed, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useAccounts } from './useAccounts';
import { useDateFns } from './useDateFns';
import { useEndpoint } from './useEndpoint';
import { useOpportunities } from './useOpportunities';
import { useToastNotifications } from './useToastNotifications';
import { useUtilities } from './useUtilities';
import type { DealsAndDocsResponse, OpportunityType } from '@apparatix/types/Leads';
import type { Document as ApxDocument } from '@apparatix/types/Account';
import { useOpportunityStore } from '@apparatix/stores/useOpportunityStore';
import { MenuItem } from 'primevue/menuitem';

export enum OpportunityFlyoutActionButtonFlags {
  None = 0,
  ConvertLead = 1 << 0,
  CreateProposal = 1 << 1,
  DisableConvertOrDuplicate = 1 << 2,
  DisableCreateProposal = 1 << 3,
  DisableOpenProposal = 1 << 4,
  DuplicateOpportunity = 1 << 5,
  OpenProposal = 1 << 6,
  OpenProposalDropdown = 1 << 7,
}

export const useOpportunityDetailFlyout = (type: OpportunityType) => {
  const store = useOpportunityStore();
  const { arrayHasValues } = useUtilities();
  const { isOpportunityActive, isOpportunityLost } = useOpportunities();
  const { getHasPrimaryContact } = useAccounts();
  const { showWarning } = useToastNotifications();
  const { createArrayDateSorter } = useDateFns();

  const documents = ref<ApxDocument[]>([]);

  const activeDocuments = computed(
    () => documents.value?.filter(doc => doc.isActive)?.sort(
      createArrayDateSorter<ApxDocument>('modified', 'DESC'),
    ),
  );

  const { selectedOpportunity: _selectedOpportunity } = storeToRefs(store);

  const opportunity = computed(() => _selectedOpportunity.value[type]?.opportunity);

  const dealsAndDocsEndpoint = useEndpoint<DealsAndDocsResponse>({
    type: 'GET',
    successField: 'data',
    startInLoadingState: true,
    onSuccess: response => {
      documents.value = response.data.documents;
    },
    toast: { showOnSuccess: false },
  });

  watch(
    () => opportunity.value?.id,
    (id, prevId) => {
      if (!id) return;
      if (id === prevId && prevId !== undefined) return;

      dealsAndDocsEndpoint.setEndpointUrl(`/crm/opportunities/${id}/deals-and-docs`);
      dealsAndDocsEndpoint.makeRequest();
    },
    { immediate: true },
  );

  const hasFlag = (flags: OpportunityFlyoutActionButtonFlags) => (actionButtonFlags.value & flags) !== 0;

  const defaultDocument = computed(() => arrayHasValues(activeDocuments) ? activeDocuments.value[0] : undefined);

  const actionButtonFlags = computed<OpportunityFlyoutActionButtonFlags>(() => {
    let flags = OpportunityFlyoutActionButtonFlags.None;

    const isActive = isOpportunityActive(type, opportunity.value);
    const hasProposal = arrayHasValues(documents);

    if (type === 'Lead') {
      if (isActive) {
        flags |= OpportunityFlyoutActionButtonFlags.ConvertLead;
        if (hasProposal) {
          flags |= OpportunityFlyoutActionButtonFlags.OpenProposalDropdown;
        } else if (store.canGenerateProposalOnLead[type]) {
          flags |= OpportunityFlyoutActionButtonFlags.CreateProposal;
        }
      } else {
        flags |= OpportunityFlyoutActionButtonFlags.DisableCreateProposal | OpportunityFlyoutActionButtonFlags.DisableConvertOrDuplicate;

        if (arrayHasValues(documents, { exactly: 1 })) {
          flags |= OpportunityFlyoutActionButtonFlags.OpenProposal;
        } else if (arrayHasValues(documents)) {
          flags |= OpportunityFlyoutActionButtonFlags.OpenProposalDropdown;
        } else {
          flags |= OpportunityFlyoutActionButtonFlags.DisableOpenProposal;
        }
      }

      return flags;
    }

    if (isActive) {
      flags |= OpportunityFlyoutActionButtonFlags.CreateProposal;

      if (hasProposal) {
        flags |= OpportunityFlyoutActionButtonFlags.OpenProposalDropdown;
      } else {
        flags |= OpportunityFlyoutActionButtonFlags.DisableOpenProposal;
      }
    } else {
      flags |= OpportunityFlyoutActionButtonFlags.DisableCreateProposal;

      if (isOpportunityLost(opportunity.value)) {
        flags |= OpportunityFlyoutActionButtonFlags.DuplicateOpportunity;
      }

      if (arrayHasValues(documents, { exactly: 1 })) {
        flags |= OpportunityFlyoutActionButtonFlags.OpenProposal;
      } else if (arrayHasValues(documents)) {
        flags |= OpportunityFlyoutActionButtonFlags.OpenProposalDropdown;
      } else {
        flags |= OpportunityFlyoutActionButtonFlags.DisableOpenProposal;
      }
    }

    return flags;
  });

  const openDocument = (doc: ApxDocument) => {
    if (!doc) return;

    const url = doc.contractId === 0
      ? `/apx/proposalTool/index-continue.html?advertiser=${doc.accountId}&tagid=&pid=${doc.id}`
      : `/util/get/getContractPDFfromCFS.php?recordId=${doc.contractId}`;

    window.open(url, '_blank');
  };

  const createProposal = () => {
    const { id, accountId } = opportunity.value;
    getHasPrimaryContact(accountId, results => {
      if (results?.hasPrimaryContact) {
        window.open(`/apx/proposalTool/index-continue.html?advertiser=${accountId}&cn=${id}`, '_blank');
      } else {
        showWarning('Please add a Primary Contact to Account');
      }
    });
  };

  const openDocumentsMenuItems = computed<MenuItem[]>(() => {
    if (!hasFlag(OpportunityFlyoutActionButtonFlags.OpenProposalDropdown)) return undefined;

    const createProposalItem: MenuItem = {
      label: 'Create Proposal',
      icon: 'pi pi-plus',
      command: () => createProposal(),
      class: 'pv-menu-item-primary-text',
    };

    if (arrayHasValues(documents, { exactly: 1 }) && hasFlag(OpportunityFlyoutActionButtonFlags.CreateProposal)) {
      return [createProposalItem];
    }

    const baseItems: MenuItem[] = activeDocuments.value.map(doc => ({
      label: doc.name,
      icon: 'pi pi-external-link',
      command: () => openDocument(doc),
    }));

    if (hasFlag(OpportunityFlyoutActionButtonFlags.CreateProposal)) {
      baseItems.push({ separator: true });
      baseItems.push(createProposalItem);
    }

    return baseItems;
  });

  return {
    createProposal,
    defaultDocument,
    documents,
    hasFlag,
    isDealsAndDocsLoading: dealsAndDocsEndpoint.isLoading,
    openDocument,
    openDocumentsMenuItems,
  };
};
